import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { cvAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, Star, CheckCircle, AlertTriangle, Lightbulb, 
  Target, TrendingUp, FileText, Loader2, ChevronDown, ChevronUp,
  User, Briefcase, GraduationCap, Wrench, Award, Zap,
  RefreshCw, Search
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const sectionIcons = {
  personal_details: <User className="w-5 h-5" />,
  professional_summary: <FileText className="w-5 h-5" />,
  work_experience: <Briefcase className="w-5 h-5" />,
  education: <GraduationCap className="w-5 h-5" />,
  skills: <Wrench className="w-5 h-5" />,
  extras: <Award className="w-5 h-5" />
};

const sectionLabels = {
  personal_details: 'Personal Details',
  professional_summary: 'Professional Summary',
  work_experience: 'Work Experience',
  education: 'Education',
  skills: 'Skills',
  extras: 'Extras & Certifications'
};

const gradeColors = {
  'A+': 'text-emerald-600 bg-emerald-50 border-emerald-200',
  'A': 'text-emerald-600 bg-emerald-50 border-emerald-200',
  'B+': 'text-sky-600 bg-sky-50 border-sky-200',
  'B': 'text-sky-600 bg-sky-50 border-sky-200',
  'C+': 'text-amber-600 bg-amber-50 border-amber-200',
  'C': 'text-amber-600 bg-amber-50 border-amber-200',
  'D': 'text-red-600 bg-red-50 border-red-200',
  'F': 'text-red-600 bg-red-50 border-red-200'
};

function ScoreRing({ score, size = 120 }) {
  const radius = (size - 12) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 80 ? '#059669' : score >= 60 ? '#0ea5e9' : score >= 40 ? '#f59e0b' : '#ef4444';

  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#e2e8f0" strokeWidth="10" />
        <circle
          cx={size/2} cy={size/2} r={radius} fill="none"
          stroke={color} strokeWidth="10" strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={offset}
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold" style={{ color }}>{score}</span>
        <span className="text-xs text-slate-500 -mt-1">/ 100</span>
      </div>
    </div>
  );
}

function SectionScoreBar({ score }) {
  const color = score >= 80 ? 'bg-emerald-500' : score >= 60 ? 'bg-sky-500' : score >= 40 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-3 w-full">
      <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-700 ease-out ${color}`} style={{ width: `${score}%` }} />
      </div>
      <span className="text-sm font-semibold text-slate-700 w-10 text-right">{score}%</span>
    </div>
  );
}

function SectionCard({ sectionKey, data }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-slate-300 transition-colors">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 p-4 text-left"
        data-testid={`section-${sectionKey}`}
      >
        <div className="p-2 bg-slate-50 rounded-lg text-slate-600">
          {sectionIcons[sectionKey]}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-semibold text-slate-900 text-sm">{sectionLabels[sectionKey]}</h3>
            {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </div>
          <SectionScoreBar score={data.score} />
        </div>
      </button>
      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-100 pt-3 animate-in fade-in slide-in-from-top-2 duration-200">
          <p className="text-sm text-slate-600 mb-3">{data.feedback}</p>
          {data.tips && data.tips.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Tips to Improve</p>
              {data.tips.map((tip, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  <Lightbulb className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                  <span className="text-slate-700">{tip}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function CVReviewPage() {
  const navigate = useNavigate();
  const { isSubscribed } = useAuth();
  const [cvs, setCvs] = useState([]);
  const [selectedCvId, setSelectedCvId] = useState('');
  const [targetJob, setTargetJob] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchingCvs, setFetchingCvs] = useState(true);
  const [review, setReview] = useState(null);

  useEffect(() => {
    fetchCvs();
  }, []);

  const fetchCvs = async () => {
    try {
      const res = await cvAPI.list();
      setCvs(res.data);
      if (res.data.length > 0) {
        setSelectedCvId(res.data[0].id);
      }
    } catch (err) {
      toast.error('Failed to load CVs');
    } finally {
      setFetchingCvs(false);
    }
  };

  const handleReview = async () => {
    if (!selectedCvId) {
      toast.error('Please select a CV to review');
      return;
    }
    setLoading(true);
    setReview(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/cv/review`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ cv_id: selectedCvId, target_job: targetJob || null })
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Review failed');
      }
      const data = await res.json();
      setReview(data.review);
      toast.success('CV review complete!');
    } catch (err) {
      toast.error(err.message || 'Failed to review CV. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (fetchingCvs) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/dashboard')} className="text-slate-500 hover:text-slate-700" data-testid="back-button">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>AI CV Review</h1>
            <p className="text-xs text-slate-500">Get expert feedback and improvement tips</p>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* CV Selection Card */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Select CV to Review</label>
              {cvs.length === 0 ? (
                <div className="text-center py-6">
                  <FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500 mb-3">No CVs found. Create one first.</p>
                  <Button onClick={() => navigate('/cv-builder')} size="sm" data-testid="create-cv-btn">
                    Create CV
                  </Button>
                </div>
              ) : (
                <select
                  value={selectedCvId}
                  onChange={(e) => setSelectedCvId(e.target.value)}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-slate-900 focus:border-transparent outline-none"
                  data-testid="cv-select"
                >
                  {cvs.map((cv) => (
                    <option key={cv.id} value={cv.id}>
                      {cv.title || cv.personal_details?.full_name || 'Untitled CV'} — ATS: {cv.ats_score || 0}%
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Target Job Title <span className="text-slate-400 font-normal">(optional)</span>
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="e.g. Admin Assistant, Junior Developer..."
                  value={targetJob}
                  onChange={(e) => setTargetJob(e.target.value)}
                  className="w-full border border-slate-200 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:ring-2 focus:ring-slate-900 focus:border-transparent outline-none"
                  data-testid="target-job-input"
                />
              </div>
              <p className="text-xs text-slate-400 mt-1">Add a target job to get role-specific recommendations</p>
            </div>

            <Button
              onClick={handleReview}
              disabled={loading || cvs.length === 0}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white py-2.5"
              data-testid="review-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Analyzing your CV...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  Review My CV
                </span>
              )}
            </Button>
          </div>
        </div>

        {/* Results */}
        {review && (
          <div className="space-y-5 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Overall Score */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <ScoreRing score={review.overall_score} />
                <div className="flex-1 text-center sm:text-left">
                  <div className="flex items-center gap-2 justify-center sm:justify-start mb-2">
                    <span className={`text-2xl font-bold px-3 py-0.5 rounded-lg border ${gradeColors[review.grade] || 'text-slate-600 bg-slate-50 border-slate-200'}`}>
                      {review.grade}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">{review.summary}</p>
                </div>
              </div>
            </div>

            {/* Section Breakdown */}
            <div>
              <h2 className="text-base font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Section Breakdown
              </h2>
              <div className="space-y-3">
                {review.sections && Object.entries(review.sections).map(([key, data]) => (
                  <SectionCard key={key} sectionKey={key} data={data} />
                ))}
              </div>
            </div>

            {/* Strengths & Improvements */}
            <div className="grid sm:grid-cols-2 gap-4">
              {/* Strengths */}
              <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-5">
                <h3 className="font-semibold text-emerald-800 text-sm mb-3 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" /> Strengths
                </h3>
                <ul className="space-y-2">
                  {(review.strengths || []).map((s, i) => (
                    <li key={i} className="text-sm text-emerald-700 flex items-start gap-2">
                      <Star className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-500" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Key Improvements */}
              <div className="bg-amber-50 rounded-xl border border-amber-200 p-5">
                <h3 className="font-semibold text-amber-800 text-sm mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" /> Key Improvements
                </h3>
                <ul className="space-y-2">
                  {(review.improvements || []).map((s, i) => (
                    <li key={i} className="text-sm text-amber-700 flex items-start gap-2">
                      <TrendingUp className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-500" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* ATS Tips */}
            {review.ats_tips && review.ats_tips.length > 0 && (
              <div className="bg-sky-50 rounded-xl border border-sky-200 p-5">
                <h3 className="font-semibold text-sky-800 text-sm mb-3 flex items-center gap-2">
                  <Target className="w-4 h-4" /> ATS Optimisation Tips
                </h3>
                <ul className="space-y-2">
                  {review.ats_tips.map((tip, i) => (
                    <li key={i} className="text-sm text-sky-700 flex items-start gap-2">
                      <Lightbulb className="w-3.5 h-3.5 mt-0.5 shrink-0 text-sky-500" />
                      <span>{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* SA Market Tips */}
            {review.sa_market_tips && review.sa_market_tips.length > 0 && (
              <div className="bg-slate-50 rounded-xl border border-slate-200 p-5">
                <h3 className="font-semibold text-slate-800 text-sm mb-3 flex items-center gap-2">
                  <span className="text-base">ZA</span> South Africa Market Tips
                </h3>
                <ul className="space-y-2">
                  {review.sa_market_tips.map((tip, i) => (
                    <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                      <Lightbulb className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-500" />
                      <span>{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Review Again */}
            <div className="flex justify-center pt-2 pb-8">
              <Button
                onClick={handleReview}
                variant="outline"
                className="gap-2"
                disabled={loading}
                data-testid="review-again-btn"
              >
                <RefreshCw className="w-4 h-4" />
                Review Again
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
